﻿import { defineStore } from 'pinia';
import { io, Socket } from 'socket.io-client';
import { ref } from 'vue';
import { getRuntimeConfig, isElectron } from '../config/runtime';
import type { TransportRequestClass } from '../services/apiTransport';
import { useEntitiesStore } from './entities';
import { useSocketStore } from './socket';
import { webRTCStatusBridge } from '../services/webrtcStatusBridge';
import type { ElectronBridge } from '../types/electron-bridge';

export interface WebRTCConfig {
  iceServers: RTCIceServer[];
}

export interface SignalingMessage {
  type: 'offer' | 'answer' | 'ice-candidate';
  /** A session description or an ICE candidate: every field is optional on
   *  both, so the same value serves `RTCSessionDescription` and
   *  `RTCIceCandidate`. */
  data: RTCSessionDescriptionInit & RTCIceCandidateInit;
}

/**
 * The cloud's WebRTC session answer: the token the peer registers with, the ICE
 * servers to use, and when the TURN credential expires.
 */
export interface WebRTCSessionAnswer {
  sessionToken: string;
  iceServers: RTCIceServer[];
  turnExpiresAt?: string;
  [key: string]: unknown;
}

export interface WebRTCRequest {
  method: string;
  path: string;
  headers: Record<string, string>;
  body?: string;
  requestClass?: TransportRequestClass;
  /** Override the class-based request timeout (e.g. large file uploads). */
  timeoutMs?: number;
}

export interface WebRTCResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
  /** Direct ArrayBuffer for binary responses — set when X-Download-Id was used.
   *  Consumers (webrtcFetch) should prefer this over atob-decoding body. */
  bodyBinary?: ArrayBuffer;
}

interface ChunkedRequestResponseMeta {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  totalChunks: number;
}

/**
 * One chunk envelope on a transfer channel: the hub's own JSON, named by the ids
 * the store logs and the index it orders by.
 */
export interface TransferChunkEnvelope {
  uploadId?: string;
  downloadId?: string;
  chunkIndex?: number;
  [key: string]: unknown;
}

type ChunkHandler = (chunk: TransferChunkEnvelope | BinaryEnvelope) => void;
/** The string at `key`, when the value carries one (a hub error body's own text). */
function readTextField(value: unknown, key: string): string {
  if (typeof value !== 'object' || value === null) return '';
  const field: unknown = Reflect.get(value, key);
  return typeof field === 'string' ? field : '';
}

const REQUEST_CLASSES: readonly TransportRequestClass[] = ['default', 'snapshot'];

type PendingRequestTask = {
  requestClass: TransportRequestClass;
  queuedAt: number;
  execute: (queueWaitMs: number) => void;
  reject: (error: Error) => void;
};

function normalizeRequestClass(requestClass?: TransportRequestClass): TransportRequestClass {
  return requestClass === 'snapshot' ? 'snapshot' : 'default';
}

// ---- Binary envelope protocol -------------------------------------------
// Layout: [4-byte LE headerLength][UTF-8 JSON header][raw binary payload]
// Analogous to HTTP: metadata headers followed by binary body, all in one
// ArrayBuffer message.  Eliminates base64 overhead (~33 %) and JSON string
// escaping cost for image/file transfers on push / download / upload channels.

export interface BinaryEnvelope {
  header: Record<string, unknown>;
  payload: Uint8Array;
}

function encodeBinaryEnvelope(header: Record<string, unknown>, payload: Uint8Array): Uint8Array {
  const headerJson = new TextEncoder().encode(JSON.stringify(header));
  const headerLen = headerJson.byteLength;
  const buffer = new Uint8Array(4 + headerLen + payload.byteLength);
  new DataView(buffer.buffer).setUint32(0, headerLen, true);
  buffer.set(headerJson, 4);
  buffer.set(payload, 4 + headerLen);
  return buffer;
}

function decodeBinaryEnvelope(data: ArrayBuffer): BinaryEnvelope | null {
  if (data.byteLength < 4) return null;
  try {
    const view = new DataView(data);
    const headerLen = view.getUint32(0, true);
    if (headerLen > data.byteLength - 4) return null;
    const headerJson = new TextDecoder().decode(new Uint8Array(data, 4, headerLen));
    const payload = new Uint8Array(data, 4 + headerLen);
    return { header: JSON.parse(headerJson), payload };
  } catch {
    return null;
  }
}

/** Handler for binary download chunks (envelope already decoded) */
type BinaryChunkHandler = (envelope: BinaryEnvelope) => void;

/** Max binary chunk payload size — leaves ~16 KB headroom under SCTP 256 KB limit */

// Electron has had intermittent DNS/ICE resolution issues with hostname-based
// STUN/TURN URLs. Keep the explicit IP fallback there, but do not rewrite the
// browser cloud client: browsers handle these hostnames correctly and the raw
// hostname matters for normal WebRTC/TURN behavior.
const ICE_HOST_OVERRIDES: Record<string, string> = {
  'stun.l.google.com': '74.125.250.129',
  'stun1.l.google.com': '74.125.250.129',
  'turn.torik.ai': '193.77.187.118',
  'turn.1hub.io': '193.77.187.118',
};
const DEBUG_WEBRTC = import.meta.env.VITE_DEBUG_WEBRTC === 'true';
const RECONNECT_DELAY_MS = 5000;
const MAX_RECONNECT_ATTEMPTS = 5;
const TURN_REFRESH_MARGIN_MS = 5 * 60 * 1000;
const MAX_IN_FLIGHT_REQUESTS = 6;
const MAX_DEFAULT_IN_FLIGHT_REQUESTS = 3;
const MAX_SNAPSHOT_IN_FLIGHT_REQUESTS = 4;
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
const SNAPSHOT_REQUEST_TIMEOUT_MS = 20_000;
const LARGE_RESPONSE_TIMEOUT_MS = 120_000;
const DEFAULT_BINARY_DOWNLOAD_TIMEOUT_MS = 300_000;
const SNAPSHOT_BINARY_DOWNLOAD_TIMEOUT_MS = 45_000;
const BINARY_DOWNLOAD_IDLE_TIMEOUT_MS = 20_000;

function debugWebRTC(...args: unknown[]): void {
  if (DEBUG_WEBRTC) {
    console.log(...args);
  }
}

function rewriteIceUrl(url: string): string {
  const match = url.match(/^(stun|turn|turns):([^?:/]+)(.*)$/i);
  if (!match) {
    return url;
  }

  const [, scheme, host, suffix] = match;
  const replacement = ICE_HOST_OVERRIDES[host.toLowerCase()];
  return replacement ? `${scheme}:${replacement}${suffix}` : url;
}

function normalizeIceServers(iceServers: RTCIceServer[] | undefined): RTCIceServer[] {
  if (!Array.isArray(iceServers)) {
    return [];
  }

  if (!isElectron()) {
    return iceServers;
  }

  return iceServers.map((server) => {
    if (Array.isArray(server.urls)) {
      return {
        ...server,
        urls: server.urls.map((url) => (typeof url === 'string' ? rewriteIceUrl(url) : url)),
      };
    }

    if (typeof server.urls === 'string') {
      return {
        ...server,
        urls: rewriteIceUrl(server.urls),
      };
    }

    return server;
  });
}

export const useWebRTCStore = defineStore('webrtc', () => {
  const socket = ref<Socket | null>(null);
  const peerConnection = ref<RTCPeerConnection | null>(null);
  const dataChannels = ref<Map<string, RTCDataChannel>>(new Map());
  const connected = ref(false);
  const connecting = ref(false);
  // Monotonic attempt counter — lets a superseded connectToHub (torn down by a
  // newer hub-switch attempt) swallow its late error instead of surfacing it.
  const connectEpoch = ref(0);
  const pendingRemoteIceCandidates = ref<RTCIceCandidateInit[]>([]);

  const hubId = ref<string | null>(null);
  const sessionToken = ref<string | null>(null);
  const activeUserToken = ref<string | null>(null);
  const reconnectAttempts = ref(0);
  const reconnectTimer = ref<number | null>(null);
  const turnRefreshTimer = ref<number | null>(null);
  const autoReconnectEnabled = ref(false);

  // Chunk handlers
  const downloadChunkHandlers = ref<ChunkHandler[]>([]);
  const uploadChunkHandlers = ref<ChunkHandler[]>([]);
  const binaryDownloadHandlers = ref<BinaryChunkHandler[]>([]);
  const pendingRequestQueues: Record<TransportRequestClass, PendingRequestTask[]> = {
    default: [],
    snapshot: [],
  };
  const inFlightRequestCounts: Record<TransportRequestClass, number> = {
    default: 0,
    snapshot: 0,
  };

  // Get URLs from runtime config
  function getCloudUrls(): { apiUrl: string; wsUrl: string } {
    try {
      const config = getRuntimeConfig();
      return {
        apiUrl: config.cloudApiUrl || config.apiUrl,
        wsUrl: config.cloudWsUrl || config.wsUrl,
      };
    } catch {
      return {
        apiUrl: 'https://torik.ai/api',
        wsUrl: 'wss://torik.ai',
      }; // Fallback
    }
  }

  function clearReconnectTimer(): void {
    if (reconnectTimer.value !== null) {
      window.clearTimeout(reconnectTimer.value);
      reconnectTimer.value = null;
    }
  }

  function clearTurnRefreshTimer(): void {
    if (turnRefreshTimer.value !== null) {
      window.clearTimeout(turnRefreshTimer.value);
      turnRefreshTimer.value = null;
    }
  }

  function getRequestTimeoutMs(requestClass: TransportRequestClass): number {
    return requestClass === 'snapshot' ? SNAPSHOT_REQUEST_TIMEOUT_MS : DEFAULT_REQUEST_TIMEOUT_MS;
  }

  function getBinaryDownloadTimeoutMs(requestClass: TransportRequestClass): number {
    return requestClass === 'snapshot' ? SNAPSHOT_BINARY_DOWNLOAD_TIMEOUT_MS : DEFAULT_BINARY_DOWNLOAD_TIMEOUT_MS;
  }

  function getTotalInFlightRequestCount(): number {
    return REQUEST_CLASSES.reduce((sum, requestClass) => sum + inFlightRequestCounts[requestClass], 0);
  }

  function canStartRequest(requestClass: TransportRequestClass): boolean {
    if (getTotalInFlightRequestCount() >= MAX_IN_FLIGHT_REQUESTS) {
      return false;
    }

    if (requestClass === 'snapshot') {
      if (inFlightRequestCounts.snapshot < MAX_SNAPSHOT_IN_FLIGHT_REQUESTS) {
        return true;
      }

      return pendingRequestQueues.default.length === 0;
    }

    if (inFlightRequestCounts.default < MAX_DEFAULT_IN_FLIGHT_REQUESTS) {
      return true;
    }

    return pendingRequestQueues.snapshot.length === 0;
  }

  function getNextPendingRequestClass(): TransportRequestClass | null {
    if (pendingRequestQueues.snapshot.length > 0 && canStartRequest('snapshot')) {
      return 'snapshot';
    }

    if (pendingRequestQueues.default.length > 0 && canStartRequest('default')) {
      return 'default';
    }

    return null;
  }

  function processPendingRequests(): void {
    while (getTotalInFlightRequestCount() < MAX_IN_FLIGHT_REQUESTS) {
      const nextRequestClass = getNextPendingRequestClass();
      if (!nextRequestClass) {
        break;
      }

      const nextRequest = pendingRequestQueues[nextRequestClass].shift();
      if (!nextRequest) {
        break;
      }

      inFlightRequestCounts[nextRequestClass] += 1;
      const queueWaitMs = performance.now() - nextRequest.queuedAt;
      if (queueWaitMs >= 250) {
        debugWebRTC(`[WebRTC] Dequeued ${nextRequestClass} request after ${queueWaitMs.toFixed(0)}ms`, {
          inFlight: getTotalInFlightRequestCount(),
          snapshotInFlight: inFlightRequestCounts.snapshot,
          defaultInFlight: inFlightRequestCounts.default,
          pendingSnapshot: pendingRequestQueues.snapshot.length,
          pendingDefault: pendingRequestQueues.default.length,
        });
      }
      nextRequest.execute(queueWaitMs);
    }
  }

  function releaseRequestSlot(requestClass: TransportRequestClass): void {
    inFlightRequestCounts[requestClass] = Math.max(0, inFlightRequestCounts[requestClass] - 1);
    processPendingRequests();
  }

  function flushPendingRequestQueue(message: string): void {
    for (const requestClass of REQUEST_CLASSES) {
      while (pendingRequestQueues[requestClass].length > 0) {
        const pendingRequest = pendingRequestQueues[requestClass].shift();
        pendingRequest?.reject(new Error(message));
      }
      inFlightRequestCounts[requestClass] = 0;
    }
  }

  function teardownConnection(clearTarget = true): void {
    clearTurnRefreshTimer();
    flushPendingRequestQueue('WebRTC connection closed');

    if (peerConnection.value) {
      peerConnection.value.onicecandidate = null;
      peerConnection.value.onconnectionstatechange = null;
      peerConnection.value.ondatachannel = null;
      peerConnection.value.close();
      peerConnection.value = null;
    }

    if (socket.value) {
      socket.value.removeAllListeners();
      socket.value.disconnect();
      socket.value = null;
    }

    webRTCStatusBridge.clearChannel();
    dataChannels.value.clear();
    binaryDownloadHandlers.value = [];
    connected.value = false;
    connecting.value = false;
    sessionToken.value = null;
    pendingRemoteIceCandidates.value = [];

    if (clearTarget) {
      clearReconnectTimer();
      reconnectAttempts.value = 0;
      autoReconnectEnabled.value = false;
      hubId.value = null;
      activeUserToken.value = null;
    }
  }

  function scheduleTurnCredentialRefresh(turnExpiresAt?: string): void {
    clearTurnRefreshTimer();

    if (!turnExpiresAt || !autoReconnectEnabled.value) {
      return;
    }

    const expiresAtMs = Date.parse(turnExpiresAt);
    if (!Number.isFinite(expiresAtMs)) {
      return;
    }

    const delay = Math.max(60_000, expiresAtMs - Date.now() - TURN_REFRESH_MARGIN_MS);
    turnRefreshTimer.value = window.setTimeout(() => {
      scheduleReconnect('TURN credentials expiring', 0);
    }, delay);
  }

  /**
   * Fetch WebRTC session from cloud API with 401 retry.
   * If the token is expired, refresh it via authStore and retry once.
   */
  async function fetchWebRTCSession(
    apiUrl: string,
    hubIdParam: string,
    userToken: string,
    electron: ElectronBridge | undefined,
  ): Promise<WebRTCSessionAnswer> {
    const doFetch = async (token: string): Promise<WebRTCSessionAnswer> => {
      if (electron?.httpRequest) {
        const result = await electron.httpRequest({
          url: `${apiUrl}/webrtc/session`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ hubId: hubIdParam }),
        });

        if (result.status < 200 || result.status >= 300) {
          const errorDetail =
            readTextField(result.data, 'error') ||
            readTextField(result.data, 'message') ||
            result.statusText ||
            'Unknown error';
          const err = Object.assign(new Error(`Failed to create WebRTC session (${result.status}): ${errorDetail}`), {
            status: result.status,
            // The server's own machine-readable code, carried on the thrown value
            // so the connect path can name the refusal
            // (`services/cloudSessionRefusal.ts`) instead of reading the free text.
            code: readTextField(result.data, 'code') || null,
          });
          throw err;
        }

        // The bridge hands back the endpoint's own body.
        return result.data as WebRTCSessionAnswer;
      } else {
        const response = await fetch(`${apiUrl}/webrtc/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ hubId: hubIdParam }),
        });

        if (!response.ok) {
          let errorDetail = response.statusText || 'Unknown error';
          // The body's own code travels on the thrown value beside the status
          // (`code`) — the same field the Electron bridge branch above sets.
          let errorCode: string | null = null;
          try {
            const errorBody = await response.json();
            errorDetail = errorBody?.error || errorBody?.message || errorDetail;
            errorCode = typeof errorBody?.code === 'string' && errorBody.code.length > 0 ? errorBody.code : null;
          } catch {
            /* ignore */
          }
          const err = Object.assign(new Error(`Failed to create WebRTC session (${response.status}): ${errorDetail}`), {
            status: response.status,
            code: errorCode,
          });
          throw err;
        }

        return await response.json();
      }
    };

    try {
      return await doFetch(userToken);
    } catch (__caught: unknown) {
      const error = __caught as { status?: number; message?: string };
      if (error.status === 401 || error.message?.includes('401')) {
        console.warn('[WebRTC] Session creation got 401, refreshing cloud token...');
        const { useAuthStore } = await import('./auth');
        const refreshed = await useAuthStore().refreshCloudAccessToken();
        if (refreshed) {
          const newToken = useAuthStore().cloudAccessToken;
          if (newToken) {
            activeUserToken.value = newToken;
            return await doFetch(newToken);
          }
        }
      }
      throw error;
    }
  }

  function scheduleReconnect(reason: string, delay = RECONNECT_DELAY_MS): void {
    if (!autoReconnectEnabled.value || !hubId.value || !activeUserToken.value || reconnectTimer.value !== null) {
      return;
    }

    if (reconnectAttempts.value >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[WebRTC] Max reconnect attempts reached:', reason);
      return;
    }

    reconnectTimer.value = window.setTimeout(async () => {
      reconnectTimer.value = null;

      const targetHubId = hubId.value;
      const userToken = activeUserToken.value;
      if (!autoReconnectEnabled.value || !targetHubId || !userToken) {
        return;
      }

      reconnectAttempts.value += 1;
      debugWebRTC(`[WebRTC] Reconnecting (${reconnectAttempts.value}/${MAX_RECONNECT_ATTEMPTS}) after: ${reason}`);
      teardownConnection(false);

      try {
        await connectToHub(targetHubId, userToken);
      } catch (error) {
        console.warn('[WebRTC] Reconnect attempt failed:', error);
        scheduleReconnect('reconnect attempt failed', Math.min(RECONNECT_DELAY_MS * reconnectAttempts.value, 15_000));
      }
    }, delay);
  }

  /**
   * Clear the caches of hub-scoped data stores so a new hub connection starts
   * clean. Without this, stores that load once (guarded by a `loaded` flag)
   * keep serving the previous hub's data after a hub switch — e.g. the cameras
   * page shows hub A's cameras while connected to hub B, or stays empty after
   * having been on a hub with no cameras.
   */
  async function resetHubScopedDataStores(): Promise<void> {
    debugWebRTC('[WebRTC] Resetting hub-scoped data stores');
    try {
      useEntitiesStore().resetForHubSwitch();
    } catch {
      /* non-fatal */
    }
    // The hub identity is hub-scoped too: a hub user id exists on ONE hub, so
    // the previous hub's `authStore.user` must not survive into this session.
    // Surfaces keyed on it (top-bar avatar, the user drawer's devices list, the
    // account page) otherwise request `/users/<other-hub id>/…` on the new hub —
    // 404, empty avatar/devices, and a profile page that renders "User not
    // found". fetchHubUser() below repopulates it from this hub's `/users/me`.
    try {
      // `useAuthStore` is imported dynamically in this store (circular-import
      // reasons) — a bare call here would throw into the catch and silently do
      // nothing.
      const { useAuthStore } = await import('./auth');
      useAuthStore().clearHubIdentity();
    } catch {
      /* non-fatal */
    }
    const resets = [
      () => import('./dashboard').then((m) => m.useDashboardStore().resetForHubSwitch()),
      () => import('./deviceAutomations').then((m) => m.useDeviceAutomationsStore().resetForHubSwitch()),
      () => import('./cameras').then((m) => m.useCamerasStore().resetForHubSwitch()),
      () => import('./cameraSnapshots').then((m) => m.useCameraSnapshotsStore().resetForHubSwitch()),
    ];
    await Promise.allSettled(resets.map((fn) => fn()));
  }

  /**
   * The runtime API base as it was before a WebRTC session took over, so a
   * disconnect can put it back (a LAN hub selection later must still win).
   */
  let apiUrlBeforeWebRTC: string | null = null;

  async function connectToHub(hubIdParam: string, userToken: string) {
    // Idempotent only for the SAME hub — a repeat call while connecting or
    // connected to it is a no-op (e.g. Servers connect + the router guard both
    // calling it for the same navigation).
    if ((connecting.value || connected.value) && hubId.value === hubIdParam) {
      debugWebRTC('[WebRTC] Already connecting or connected to this hub');
      return;
    }

    // Remember the hub the per-hub stores currently hold data for BEFORE any
    // teardown below nulls hubId.
    const previousHubId = hubId.value;

    // Connecting to a DIFFERENT hub than the one whose data is cached: reset
    // the hub-scoped stores so the new hub re-fetches instead of showing the
    // previous hub's devices/cameras or a stale empty page.
    if (hubIdParam !== previousHubId) {
      await resetHubScopedDataStores();
    }

    // Switching to a DIFFERENT hub while a session exists must tear the old one
    // down first — otherwise this call was a silent no-op and hubId / the data
    // channels kept serving the previous hub.
    if (connecting.value || connected.value) {
      debugWebRTC('[WebRTC] Switching hubs, tearing down current session');
      teardownConnection(true);
    }

    const epoch = ++connectEpoch.value;

    clearReconnectTimer();
    connecting.value = true;
    hubId.value = hubIdParam;
    activeUserToken.value = userToken;
    autoReconnectEnabled.value = true;
    // Remember the hub key for per-hub UI state — cloud/WebRTC sessions use the
    // cloud hub id as the stable key (the local apiUrl may be stale/unchanged).
    try {
      localStorage.setItem('otto_active_hub_key', `hub:${hubIdParam}`);
    } catch {
      // ignore
    }

    try {
      const { apiUrl, wsUrl } = getCloudUrls();
      const electron = window.electron;

      debugWebRTC('[WebRTC] Using API URL:', apiUrl);
      debugWebRTC('[WebRTC] Using WS URL:', wsUrl);

      // Fetch session with 401 retry (token may have expired)
      const session = await fetchWebRTCSession(apiUrl, hubIdParam, userToken, electron);

      debugWebRTC('[WebRTC] Session created:', session.sessionToken?.substring(0, 20) + '...');
      sessionToken.value = session.sessionToken;
      session.iceServers = normalizeIceServers(session.iceServers);
      debugWebRTC('[WebRTC] Normalized ICE servers:', session.iceServers);
      scheduleTurnCredentialRefresh(session.turnExpiresAt);

      // Connect to signaling server via WebSocket
      debugWebRTC('[WebRTC] Connecting to signaling server at', wsUrl);
      socket.value = io(wsUrl, {
        auth: { token: userToken },
        transports: ['websocket'],
      });

      socket.value.on('connect_error', (error) => {
        console.error('[WebRTC] Signaling connect_error:', error?.message || error);
      });

      socket.value.on('disconnect', (reason) => {
        console.warn('[WebRTC] Signaling disconnected:', reason);
      });

      socket.value.on('connect', () => {
        debugWebRTC('[WebRTC] Signaling connected');
        // Register as client
        debugWebRTC('[WebRTC] Registering client with sessionToken:', session.sessionToken?.substring(0, 20) + '...');
        socket.value?.emit('client:register', {
          userId: getUserIdFromToken(userToken),
          sessionToken: session.sessionToken,
        });
      });

      const connectionReady = new Promise<void>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          reject(new Error('Timed out waiting for WebRTC signaling registration'));
        }, 15000);

        socket.value?.on('client:registered', async () => {
          try {
            window.clearTimeout(timeoutId);
            debugWebRTC('[WebRTC] Client registered, creating peer connection');
            await createPeerConnection(session.iceServers, resolve);
            await createOffer();
          } catch (error) {
            reject(error);
          }
        });

        socket.value?.on('error', (error) => {
          window.clearTimeout(timeoutId);
          reject(new Error(error?.message || 'WebRTC signaling error'));
        });
      });

      socket.value.on('webrtc:answer', async ({ answer }) => {
        debugWebRTC('[WebRTC] Received answer from hub');
        if (peerConnection.value) {
          if (peerConnection.value.signalingState !== 'have-local-offer') {
            console.warn('[WebRTC] Ignoring duplicate answer, signaling state is', peerConnection.value.signalingState);
            return;
          }

          await peerConnection.value.setRemoteDescription(new RTCSessionDescription(answer));

          if (pendingRemoteIceCandidates.value.length > 0) {
            for (const pendingCandidate of pendingRemoteIceCandidates.value) {
              await peerConnection.value.addIceCandidate(new RTCIceCandidate(pendingCandidate));
            }
            pendingRemoteIceCandidates.value = [];
          }
        }
      });

      socket.value.on('webrtc:ice-candidate', async ({ candidate }) => {
        debugWebRTC('[WebRTC] Received ICE candidate');
        if (peerConnection.value && candidate) {
          if (peerConnection.value.remoteDescription) {
            await peerConnection.value.addIceCandidate(new RTCIceCandidate(candidate));
          } else {
            pendingRemoteIceCandidates.value.push(candidate);
          }
        }
      });

      socket.value.on('error', (error) => {
        console.error('[WebRTC] Signaling error:', error);
        connecting.value = false;
      });

      await connectionReady;

      // Do not let callers proceed on signaling-registration alone — the peer
      // connection must actually establish. Otherwise every API request falls
      // back to HTTP against the cloud API (404/CORS flood) while the UI
      // appears to be "connecting" forever.
      await waitForConnectionEstablished(epoch);
    } catch (error) {
      // A superseded attempt (a newer connectToHub for another hub tore this
      // session down) must not flip shared state or surface a late error to
      // the caller that started it.
      if (epoch !== connectEpoch.value) {
        debugWebRTC('[WebRTC] Superseded connect attempt ignored:', error);
        return;
      }
      // Tear down the hung session so a failed connect doesn't leave a socket /
      // peer connection reaching for an unresponsive hub in the background.
      teardownConnection(true);
      console.error('Failed to connect to hub:', error);
      connecting.value = false;
      throw error;
    }
  }

  /** True when the 'request' data channel (used by all API calls) is open. */
  function isRequestChannelReady(): boolean {
    const channel = dataChannels.value.get('request');
    return !!channel && channel.readyState === 'open';
  }

  /**
   * Resolves once the peer connection is established AND the request data
   * channel is open (so API calls over WebRTC can actually flow), or rejects
   * when the hub never answers within the timeout, or when a newer connectToHub
   * supersedes this attempt. Without this, callers would proceed on the peer
   * connection state alone and the first page after connect would hit
   * "Request channel not available" and fall back to HTTP (404/CORS flood).
   */
  function waitForConnectionEstablished(epoch: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const PEER_CONNECT_TIMEOUT_MS = 30_000;
      let settled = false;
      let pollTimer: number | null = null;
      let deadlineTimer: number | null = null;

      const cleanup = () => {
        if (pollTimer !== null) window.clearTimeout(pollTimer);
        if (deadlineTimer !== null) window.clearTimeout(deadlineTimer);
      };
      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        cleanup();
        fn();
      };

      const check = () => {
        if (settled) return;
        if (connected.value && isRequestChannelReady()) {
          finish(() => resolve());
          return;
        }
        if (epoch !== connectEpoch.value) {
          finish(() => reject(new Error('Connection attempt superseded')));
          return;
        }
        pollTimer = window.setTimeout(check, 150);
      };

      deadlineTimer = window.setTimeout(() => {
        finish(() => reject(new Error('Hub did not answer the connection (timed out after 30s)')));
      }, PEER_CONNECT_TIMEOUT_MS);

      check();
    });
  }

  async function createPeerConnection(iceServers: RTCIceServer[], onReady?: () => void) {
    pendingRemoteIceCandidates.value = [];
    peerConnection.value = new RTCPeerConnection({ iceServers });

    // Handle ICE candidates
    peerConnection.value.onicecandidate = (event) => {
      if (event.candidate && socket.value) {
        debugWebRTC('[WebRTC] Sending ICE candidate for session:', sessionToken.value?.substring(0, 20) + '...');
        socket.value.emit('webrtc:ice-candidate', {
          sessionToken: sessionToken.value,
          candidate: event.candidate,
        });
      }
    };

    // Handle connection state changes
    peerConnection.value.onconnectionstatechange = async () => {
      const state = peerConnection.value?.connectionState;
      debugWebRTC('[WebRTC] Connection state:', state);

      if (state === 'connected') {
        connected.value = true;
        connecting.value = false;
        reconnectAttempts.value = 0;
        clearReconnectTimer();
        socket.value?.emit('webrtc:connected', { sessionToken: sessionToken.value });

        // Point the runtime API base at the cloud API for this session. In the
        // Capacitor build config.apiUrl starts empty ("set dynamically after hub
        // selection") and only the LAN path — setActiveHub() — ever fills it in.
        // Without this, every hub call routed over the data channel lost its /api
        // prefix (`/rooms` instead of `/api/rooms`), the hub answered its HTML
        // 404, and readJsonSafe threw "Received HTML instead of JSON" — the
        // session connected and then rendered nothing (/users/me included, which
        // is why the profile stayed empty).
        const runtimeConfig = getRuntimeConfig();
        const cloudApiUrl = getCloudUrls().apiUrl;
        if (cloudApiUrl) {
          if (apiUrlBeforeWebRTC === null) {
            apiUrlBeforeWebRTC = runtimeConfig.apiUrl;
          }
          runtimeConfig.apiUrl = cloudApiUrl;
        }

        // Wait for all data channels to actually open before initializing stores
        debugWebRTC('[WebRTC] Connection established, waiting for data channels to open...');
        await waitForDataChannelsOpen();

        const socketStore = useSocketStore();
        socketStore.connect();

        // Resolve the hub-side identity (authStore.user) so the UI shows the
        // correct user + avatar in cloud mode. In cloud mode the local
        // accessToken is null, so checkAuth() never populated authStore.user.
        // fetchHubUser() runs /users/me over this data channel (the hub injects
        // a local JWT for the mapped cloud user) and auto-links the cloud
        // account to the hub user by email when needed.
        try {
          const { useAuthStore } = await import('./auth');
          const authStore = useAuthStore();
          await authStore.fetchHubUser();
        } catch (error) {
          debugWebRTC('[WebRTC] fetchHubUser failed (non-fatal):', error);
          // Safety net: the first attempt can run before connectCloudHub() has
          // navigated to /h/:hubId. If it was misrouted (HTTP proxy in cloud
          // mode has no local hub token), retry once after navigation lands —
          // apiRouter then routes /users/me over the WebRTC channel where the
          // hub injects the local JWT. Without authStore.user the top-bar
          // avatar degrades to initials.
          const sessionHubId = hubId.value;
          window.setTimeout(() => {
            void (async () => {
              try {
                if (!connected.value || hubId.value !== sessionHubId) return;
                const { useAuthStore: useAuthStoreRetry } = await import('./auth');
                const authStoreRetry = useAuthStoreRetry();
                if (!authStoreRetry.user) {
                  await authStoreRetry.fetchHubUser();
                  debugWebRTC('[WebRTC] fetchHubUser retry succeeded');
                }
              } catch (retryError) {
                debugWebRTC('[WebRTC] fetchHubUser retry failed (non-fatal):', retryError);
              }
            })();
          }, 2000);
        }

        // Initialize hub data stores now that we're connected
        debugWebRTC('[WebRTC] Data channels ready, initializing stores...');
        try {
          const entitiesStore = useEntitiesStore();
          await entitiesStore.initialize();

          // Cameras store is normally initialized from App.vue in local /
          // electron mode. Cloud mode skips that path, so load it here once
          // the WebRTC relay is ready. Without it camera widgets get no
          // camera metadata (PTZ flags, motion listeners) in cloud mode.
          try {
            const { useCamerasStore } = await import('./cameras');
            await useCamerasStore().initialize();
          } catch (cameraError) {
            console.warn('[WebRTC] Failed to initialize cameras store (non-fatal):', cameraError);
          }

          debugWebRTC('[WebRTC] Stores initialized successfully');
          onReady?.();
        } catch (error) {
          console.error('[WebRTC] Failed to initialize stores:', error);
          onReady?.();
        }
      } else if (state === 'disconnected') {
        connected.value = false;
        connecting.value = false;
        scheduleReconnect('peer connection disconnected');
      } else if (state === 'failed' || state === 'closed') {
        connected.value = false;
        connecting.value = false;
        scheduleReconnect(`peer connection ${state}`, 1000);
      }
    };

    // Handle data channels
    peerConnection.value.ondatachannel = (event) => {
      const channel = event.channel;
      debugWebRTC('[WebRTC] Data channel received:', channel.label);
      setupDataChannel(channel);
    };

    // Create data channels: request, status, upload, download, push
    const channelNames = ['request', 'status', 'upload', 'download', 'push'];
    for (const name of channelNames) {
      const channel = peerConnection.value.createDataChannel(name, {
        // Request and upload need ordering: upload chunks are spooled to disk
        // in arrival order on the hub, so out-of-order delivery would corrupt
        // the file. Download/push are index-assembled / independent frames.
        ordered: name === 'request' || name === 'upload',
      });
      debugWebRTC(`[WebRTC] Creating data channel: ${name}`);
      setupDataChannel(channel);
    }
  }

  function waitForDataChannelsOpen(): Promise<void> {
    return new Promise((resolve) => {
      // Check if all required channels are open
      const requiredChannels = ['request', 'status', 'upload', 'download', 'push'];
      const OPEN_TIMEOUT_MS = 10_000;
      let settled = false;

      const settle = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };

      // Guard against channels that never fire onopen (e.g. after a werift reconnect).
      // Resolve anyway so the app can proceed with whatever channels did open.
      const timeoutId = window.setTimeout(() => {
        const missing = requiredChannels.filter((name) => {
          if (name === 'status') return !webRTCStatusBridge.connected;
          const ch = dataChannels.value.get(name);
          return !ch || ch.readyState !== 'open';
        });
        if (missing.length > 0) {
          console.warn('[WebRTC] Timed out waiting for data channels; missing:', missing);
        }
        settle();
      }, OPEN_TIMEOUT_MS);

      const checkChannels = () => {
        const allOpen = requiredChannels.every((name) => {
          if (name === 'status') {
            return webRTCStatusBridge.connected;
          }
          const channel = dataChannels.value.get(name);
          return channel && channel.readyState === 'open';
        });

        if (allOpen) {
          window.clearTimeout(timeoutId);
          debugWebRTC('[WebRTC] All data channels open and ready');
          settle();
        } else if (!settled) {
          // Check again in a bit
          setTimeout(checkChannels, 100);
        }
      };

      checkChannels();
    });
  }

  function setupDataChannel(channel: RTCDataChannel) {
    if (channel.label === 'status') {
      webRTCStatusBridge.attachChannel(channel);
      return;
    }

    // Enable binary on data-heavy channels
    const isBinaryChannel = channel.label === 'push' || channel.label === 'download' || channel.label === 'upload';
    if (isBinaryChannel) {
      channel.binaryType = 'arraybuffer';
    }

    channel.onopen = () => {
      debugWebRTC(`[WebRTC] Data channel ${channel.label} opened`);
      dataChannels.value.set(channel.label, channel);
    };

    channel.onclose = () => {
      debugWebRTC(`[WebRTC] Data channel ${channel.label} closed`);
      dataChannels.value.delete(channel.label);
    };

    channel.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        handleBinaryEnvelope(channel.label, event.data);
      } else {
        handleDataChannelMessage(channel.label, event.data as string);
      }
    };

    channel.onerror = (error) => {
      console.error(`[WebRTC] Data channel ${channel.label} error:`, error);
    };
  }

  async function createOffer() {
    if (!peerConnection.value) return;

    const offer = await peerConnection.value.createOffer();
    await peerConnection.value.setLocalDescription(offer);

    // Send offer to hub via signaling server using the registered session token.
    debugWebRTC(`[WebRTC] Sending offer for session ${sessionToken.value?.substring(0, 20)}...`);
    socket.value?.emit('webrtc:offer', {
      sessionToken: sessionToken.value,
      offer,
    });
  }

  function handleBinaryEnvelope(channelName: string, data: ArrayBuffer) {
    const envelope = decodeBinaryEnvelope(data);
    if (!envelope) {
      console.warn(`[WebRTC] Failed to decode binary envelope on ${channelName}`);
      return;
    }

    debugWebRTC(`[WebRTC] Binary message on ${channelName}:`, envelope.header);

    switch (channelName) {
      case 'push':
        // Camera frame push — create Blob for direct use in <img> / canvas
        {
          const mime = `image/${(envelope.header.fmt as string) || 'jpeg'}`;
          const blob = new Blob([envelope.payload as BlobPart], { type: mime });
          window.dispatchEvent(
            new CustomEvent('hub:camera', {
              detail: { camId: envelope.header.camId, ts: envelope.header.ts, blob },
            }),
          );
        }
        break;

      case 'download':
        binaryDownloadHandlers.value.forEach((handler) => handler(envelope));
        break;

      case 'upload':
        uploadChunkHandlers.value.forEach((handler) => handler(envelope));
        break;

      default:
        console.warn(`[WebRTC] Unexpected binary data on channel: ${channelName}`);
    }
  }

  function handleDataChannelMessage(channelName: string, data: string) {
    try {
      const message = JSON.parse(data);
      debugWebRTC(`[WebRTC] Message on ${channelName}:`, message.id || message.type || 'data');

      // Handle different channel types
      switch (channelName) {
        case 'status':
          // Device status updates - emit event for components
          window.dispatchEvent(new CustomEvent('hub:status', { detail: message }));
          break;

        case 'push':
          // Camera image push — handled as binary envelope (see handleBinaryEnvelope).
          // If we receive text here the hub hasn't upgraded to binary yet.
          window.dispatchEvent(new CustomEvent('hub:camera', { detail: message }));
          break;

        case 'download':
          // Download chunk - call registered handlers
          downloadChunkHandlers.value.forEach((handler) => handler(message));
          break;

        case 'upload':
          // Upload acknowledgment - call registered handlers
          uploadChunkHandlers.value.forEach((handler) => handler(message));
          break;

        case 'request':
          // Request responses are handled in sendRequest()
          break;

        default:
          console.warn(`[WebRTC] Unknown channel: ${channelName}`);
      }
    } catch (error) {
      console.error('[WebRTC] Failed to parse data channel message:', error);
    }
  }

  async function sendRequest(request: WebRTCRequest): Promise<WebRTCResponse> {
    return new Promise((resolve, reject) => {
      const requestClass = normalizeRequestClass(request.requestClass);
      pendingRequestQueues[requestClass].push({
        requestClass,
        queuedAt: performance.now(),
        reject,
        execute: (queueWaitMs) => {
          const requestChannel = dataChannels.value.get('request');
          if (!requestChannel || requestChannel.readyState !== 'open') {
            releaseRequestSlot(requestClass);
            reject(new Error('Request channel not available'));
            return;
          }

          const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(7)}`;
          debugWebRTC(`[WebRTC] Sending ${requestClass} request ${requestId}: ${request.method} ${request.path}`, {
            queueWaitMs: Number(queueWaitMs.toFixed(0)),
          });

          const message = {
            id: requestId,
            method: request.method,
            path: request.path,
            headers: request.headers,
            body: request.body,
          };

          let chunkedMeta: ChunkedRequestResponseMeta | null = null;
          let receivedChunks = 0;
          const bodyChunks: string[] = [];
          let slotReleased = false;
          let binaryIdleTimeout: ReturnType<typeof setTimeout> | null = null;
          let removeBinaryHandler = () => {};

          const releaseSlotOnce = () => {
            if (slotReleased) return;
            slotReleased = true;
            releaseRequestSlot(requestClass);
          };

          const clearBinaryIdleTimeout = () => {
            if (binaryIdleTimeout !== null) {
              clearTimeout(binaryIdleTimeout);
              binaryIdleTimeout = null;
            }
          };

          const finalize = (callback: () => void) => {
            clearTimeout(timeout);
            clearBinaryIdleTimeout();
            requestChannel.removeEventListener('message', handleResponse);
            releaseSlotOnce();
            callback();
          };

          // Allow snapshots their own budget so they fail fast on real stalls
          // without waiting behind the generic 30 s default path. Large
          // uploads (IFC models) pass an explicit timeoutMs budget.
          let timeout = setTimeout(
            () => {
              requestChannel.removeEventListener('message', handleResponse);
              clearBinaryIdleTimeout();
              removeBinaryHandler();
              releaseSlotOnce();
              reject(new Error('Request timeout'));
            },
            request.timeoutMs ?? getRequestTimeoutMs(requestClass),
          );

          const tryResolveChunkedResponse = () => {
            if (!chunkedMeta || receivedChunks !== chunkedMeta.totalChunks) {
              return;
            }

            debugWebRTC(
              `[WebRTC] Chunked response ${requestId}:`,
              chunkedMeta.status,
              chunkedMeta.statusText,
              `chunks=${chunkedMeta.totalChunks}`,
            );

            finalize(() => {
              resolve({
                status: chunkedMeta!.status || 200,
                statusText: chunkedMeta!.statusText || 'OK',
                headers: chunkedMeta!.headers || {},
                body: bodyChunks.join(''),
              });
            });
          };

          const handleResponse = (event: MessageEvent) => {
            try {
              const response = JSON.parse(event.data);
              if (response.id === requestId) {
                if (response.error) {
                  finalize(() => reject(new Error(response.error)));
                } else if (response.chunked) {
                  if (typeof response.chunkIndex === 'number') {
                    if (bodyChunks[response.chunkIndex] === undefined) {
                      bodyChunks[response.chunkIndex] = response.bodyChunk || '';
                      receivedChunks += 1;
                    }
                    tryResolveChunkedResponse();
                  } else {
                    chunkedMeta = {
                      status: response.status || 200,
                      statusText: response.statusText || 'OK',
                      headers: response.headers || {},
                      totalChunks: response.totalChunks || 0,
                    };
                    // Large transfers (IFC models, heavy images): extend timeout
                    // to 2 min once we know the chunk count exceeds the 30 s budget.
                    if (chunkedMeta.totalChunks > 50) {
                      clearTimeout(timeout);
                      timeout = setTimeout(() => {
                        requestChannel.removeEventListener('message', handleResponse);
                        clearBinaryIdleTimeout();
                        removeBinaryHandler();
                        releaseSlotOnce();
                        reject(new Error('Request timeout'));
                      }, LARGE_RESPONSE_TIMEOUT_MS);
                      debugWebRTC(`[WebRTC] Extended timeout for large response: ${chunkedMeta.totalChunks} chunks`);
                    }
                    tryResolveChunkedResponse();
                  }
                } else {
                  debugWebRTC(`[WebRTC] Response ${requestId}:`, response.status, response.statusText);

                  // Large binary responses come via the download channel.
                  // The response has no body — instead X-Download-Id tells us
                  // to listen for binary envelopes on the download channel.
                  const downloadId = response.headers?.['X-Download-Id'] || response.headers?.['x-download-id'];
                  const totalChunks = parseInt(
                    response.headers?.['X-Total-Chunks'] || response.headers?.['x-total-chunks'] || '0',
                    10,
                  );

                  if (downloadId && totalChunks > 0) {
                    // Large binary downloads can keep flowing on the download
                    // channel after we free the request lane; keep a class-aware
                    // hard timeout plus a shorter idle-stall timeout.
                    clearTimeout(timeout);
                    timeout = setTimeout(() => {
                      requestChannel.removeEventListener('message', handleResponse);
                      clearBinaryIdleTimeout();
                      removeBinaryHandler();
                      reject(new Error('Request timeout'));
                    }, getBinaryDownloadTimeoutMs(requestClass));

                    // Remove the text handler from the request channel — we
                    // already got the metadata we need.
                    requestChannel.removeEventListener('message', handleResponse);
                    releaseSlotOnce();

                    const binaryChunks: (Uint8Array | undefined)[] = new Array(totalChunks);
                    let binaryReceived = 0;
                    const binaryStartTime = performance.now();

                    removeBinaryHandler = () => {
                      binaryDownloadHandlers.value = binaryDownloadHandlers.value.filter((h) => h !== handleBinary);
                    };

                    const resetBinaryIdleTimer = () => {
                      clearBinaryIdleTimeout();
                      binaryIdleTimeout = setTimeout(() => {
                        clearTimeout(timeout);
                        clearBinaryIdleTimeout();
                        removeBinaryHandler();
                        reject(new Error('Binary download stalled'));
                      }, BINARY_DOWNLOAD_IDLE_TIMEOUT_MS);
                    };

                    const handleBinary: BinaryChunkHandler = async (envelope: BinaryEnvelope) => {
                      if (envelope.header.downloadId !== downloadId) return;
                      resetBinaryIdleTimer();
                      const idx = envelope.header.chunkIndex as number;
                      if (binaryChunks[idx] === undefined) {
                        binaryChunks[idx] = envelope.payload;
                        binaryReceived += 1;

                        // Progress log every 10% or first/last chunk
                        if (
                          binaryReceived === 1 ||
                          binaryReceived === totalChunks ||
                          binaryReceived % Math.max(1, Math.floor(totalChunks / 10)) === 0
                        ) {
                          const pct = ((binaryReceived / totalChunks) * 100).toFixed(0);
                          debugWebRTC(
                            `[WebRTC] Binary download ${downloadId}: ${binaryReceived}/${totalChunks} chunks (${pct}%)`,
                          );
                        }
                      }

                      const done = envelope.header.final === true || binaryReceived === totalChunks;
                      if (!done) return;

                      clearTimeout(timeout);
                      clearBinaryIdleTimeout();
                      removeBinaryHandler();

                      const totalLen = binaryChunks.reduce((sum, c) => sum + (c?.length ?? 0), 0);
                      const combined = new Uint8Array(totalLen);
                      let off = 0;
                      for (const c of binaryChunks) {
                        if (c) {
                          combined.set(c, off);
                          off += c.length;
                        }
                      }

                      const elapsed = ((performance.now() - binaryStartTime) / 1000).toFixed(1);
                      const mbps =
                        totalLen > 0 ? (totalLen / 1024 / 1024 / (parseFloat(elapsed) || 0.1)).toFixed(1) : '0';
                      console.log(
                        `[WebRTC] Binary download complete: ${downloadId} | ${(totalLen / 1024 / 1024).toFixed(1)} MB in ${elapsed}s (${mbps} MB/s) | ${totalChunks} chunks`,
                      );

                      // Decompress if the hub gzipped the payload (IFC/text/model files).
                      let finalBuffer: ArrayBuffer = combined.buffer.slice(
                        combined.byteOffset,
                        combined.byteOffset + combined.byteLength,
                      );
                      const binaryEncoding =
                        response.headers?.['X-Binary-Encoding'] ||
                        response.headers?.['x-binary-encoding'] ||
                        'identity';
                      if (binaryEncoding === 'gzip') {
                        try {
                          const ds = new DecompressionStream('gzip');
                          finalBuffer = await new Response(
                            new Blob([finalBuffer]).stream().pipeThrough(ds),
                          ).arrayBuffer();
                          debugWebRTC(
                            `[WebRTC] Decompressed ${downloadId}: ${(totalLen / 1024 / 1024).toFixed(1)} MB \u2192 ${(finalBuffer.byteLength / 1024 / 1024).toFixed(1)} MB`,
                          );
                        } catch (err) {
                          console.error('[WebRTC] gzip decompression failed:', err);
                        }
                      }

                      // Resolve with direct ArrayBuffer — no base64 roundtrip.
                      // webrtcFetch will use bodyBinary directly.
                      resolve({
                        status: response.status || 200,
                        statusText: response.statusText || 'OK',
                        headers: response.headers || {},
                        body: '',
                        bodyBinary: finalBuffer,
                      });
                    };

                    binaryDownloadHandlers.value.push(handleBinary);
                    resetBinaryIdleTimer();
                    return;
                  }

                  finalize(() => {
                    resolve({
                      status: response.status || 200,
                      statusText: response.statusText || 'OK',
                      headers: response.headers || {},
                      body: response.body || '',
                    });
                  });
                }
              }
            } catch (error) {
              console.error('[WebRTC] Failed to parse response:', error);
            }
          };

          requestChannel.addEventListener('message', handleResponse);
          requestChannel.send(JSON.stringify(message));
        },
      });

      processPendingRequests();
    });
  }

  async function sendChunk(channelName: string, chunk: TransferChunkEnvelope): Promise<void> {
    const channel = dataChannels.value.get(channelName);
    if (!channel || channel.readyState !== 'open') {
      throw new Error(`${channelName} channel not available`);
    }
    const candidate = channel as RTCDataChannel & { bufferedAmount?: number };
    debugWebRTC(`[WebRTC] Sending chunk on ${channelName}:`, chunk.uploadId || chunk.downloadId, chunk.chunkIndex);

    let polls = 0;
    while ((candidate.bufferedAmount ?? 0) > 512 * 1024) {
      if (polls >= 200) {
        break;
      }
      polls += 1;
      await new Promise((resolve) => {
        setTimeout(resolve, 5);
      });
    }

    channel.send(JSON.stringify(chunk));
  }

  /**
   * Send a binary chunk on a data-heavy channel (upload, download).
   * Uses the binary envelope protocol: 4-byte LE header length + JSON
   * metadata + raw payload — no base64, no JSON.parse on large data.
   */
  async function sendBinaryChunk(
    channelName: string,
    header: Record<string, unknown>,
    payload: Uint8Array,
  ): Promise<void> {
    const channel = dataChannels.value.get(channelName);
    if (!channel || channel.readyState !== 'open') {
      throw new Error(`${channelName} channel not available`);
    }

    const envelope = encodeBinaryEnvelope(header, payload);
    debugWebRTC(`[WebRTC] Binary chunk on ${channelName}:`, header, `payload=${payload.byteLength}B`);

    // Backpressure: wait until buffer drops below 512 KB
    let polls = 0;
    while (channel.bufferedAmount > 512 * 1024) {
      if (polls >= 200) break;
      polls += 1;
      await new Promise((resolve) => {
        setTimeout(resolve, 5);
      });
    }

    channel.send(envelope.buffer as ArrayBuffer);
  }

  function onDownloadChunk(handler: ChunkHandler) {
    downloadChunkHandlers.value.push(handler);
  }

  function onUploadChunk(handler: ChunkHandler) {
    uploadChunkHandlers.value.push(handler);
  }

  function onBinaryDownloadChunk(handler: BinaryChunkHandler) {
    binaryDownloadHandlers.value.push(handler);
  }

  function disconnect() {
    // Hand the API base back to whatever it was before this session took it over
    // (empty in the Capacitor build, the LAN hub's /api after a local connect).
    if (apiUrlBeforeWebRTC !== null) {
      getRuntimeConfig().apiUrl = apiUrlBeforeWebRTC;
      apiUrlBeforeWebRTC = null;
    }
    teardownConnection(true);
  }

  function getUserIdFromToken(token: string): string {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id || payload.sub || '';
    } catch {
      return '';
    }
  }

  return {
    connected,
    connecting,
    hubId,
    connectToHub,
    disconnect,
    sendRequest,
    sendChunk,
    sendBinaryChunk,
    onDownloadChunk,
    onUploadChunk,
    onBinaryDownloadChunk,
  };
});
